import { CandidateInfoQuery } from "@generated/graphql";
import { EmailOutlined, PasswordOutlined } from "@mui/icons-material";
import { Card, Stack, Typography } from "@mui/material";
import TelegramLogoIcon from "assets/icons/TelegramLogo";
import VkLogoIcon from "assets/icons/VkLogo";
import YandexLogoIcon from "assets/icons/YandexLogo";
import FieldLabel from "components/FieldLabel";
import TextFieldControl from "components/form/TextFieldControl";
import { AuthSettingName } from "constants/candidate.constants";
import typography from "constants/typography.constants";
import { modifyTelegramUsername } from "helpers/form.helpers";
import VALIDATION from "helpers/validation";
import useIsCreate from "hooks/useIsCreate";
import SettingCard from "./SettingCard";
import { AuthSetting } from "./types";

const DISABLED_SETTING_SWITCH_TITLE = "Установленный способ входа удалить нельзя";

interface AuthSettingsProps {
  fieldsNamePrefix?: string;
  isEditing: boolean;
  viewMode?: boolean;
  companyCandidate?: {
    candidateUsername?: Pick<
      NonNullable<NonNullable<CandidateInfoQuery["companyCandidate"]>["candidateUsername"]>,
      "email" | "login" | "telegramUsername" | "vkId" | "yandexEmail"
    > | null;
  } | null;
}

const AuthSettings = ({ fieldsNamePrefix = "", isEditing, viewMode = false, companyCandidate }: AuthSettingsProps) => {
  const isCreate = useIsCreate();
  const { email, login, telegramUsername, yandexEmail, vkId } = companyCandidate?.candidateUsername || {};

  const authSettingsOptions: AuthSetting[] = [
    ...(!viewMode || email
      ? [
          {
            name: AuthSettingName.EMAIL,
            title: "Email",
            subtitle: "Вход по адресу электронной почты",
            Icon: EmailOutlined,
            disabled: !!email,
            tooltipProps: { title: email && DISABLED_SETTING_SWITCH_TITLE },
            fields: (
              <TextFieldControl
                name={`${fieldsNamePrefix}email`}
                label='Email для входа'
                placeholder='Почта'
                disabled={!isEditing}
                rules={{ required: true, ...VALIDATION.emailFull }}
                labelLink={email ? { to: `mailto:${email}`, target: "_blank", text: typography.write } : undefined}
              />
            ),
          },
        ]
      : []),
    ...(!viewMode || login
      ? [
          {
            name: AuthSettingName.LOGIN,
            title: "Логин + Пароль",
            subtitle: "Вход по логину и паролю платформы",
            Icon: PasswordOutlined,
            disabled: !!login,
            tooltipProps: { title: login && DISABLED_SETTING_SWITCH_TITLE },
            fields: (
              <>
                <TextFieldControl
                  name={`${fieldsNamePrefix}login`}
                  label='Логин пользователя'
                  placeholder='Логин пользователя'
                  disabled={!isEditing}
                  rules={{ required: true, ...VALIDATION.telegramUsername }}
                />
                <TextFieldControl
                  name={`${fieldsNamePrefix}password`}
                  label='Пароль'
                  type='password'
                  disabled={!!login}
                  rules={{ required: !login }}
                />
              </>
            ),
          },
        ]
      : []),
    ...(!viewMode || telegramUsername
      ? [
          {
            name: AuthSettingName.TELEGRAM,
            title: "Telegram",
            subtitle: "Вход через профиль Telegram",
            Icon: TelegramLogoIcon,
            disabled: !!telegramUsername,
            tooltipProps: { title: telegramUsername && DISABLED_SETTING_SWITCH_TITLE },
            fields: (
              <TextFieldControl
                name={`${fieldsNamePrefix}telegramUsername`}
                label='Профиль Telegram'
                placeholder='Профиль Telegram'
                disabled={!isEditing}
                rules={{ required: true, ...VALIDATION.telegramUsername }}
                handleChange={(value: string, onChange) => onChange(modifyTelegramUsername(value))}
                labelLink={
                  telegramUsername
                    ? {
                        to: `https://t.me/${telegramUsername.replace("@", "")}`,
                        target: "_blank",
                        text: typography.write,
                      }
                    : undefined
                }
              />
            ),
          },
        ]
      : []),
    ...(yandexEmail
      ? [
          {
            name: AuthSettingName.YANDEX_ID,
            title: "Yandex ID",
            subtitle: "Вход через аккаунт Яндекса",
            Icon: YandexLogoIcon,
            disabled: true,
            fields: (
              <TextFieldControl
                name={`${fieldsNamePrefix}yandexEmail`}
                label='Yandex email для входа'
                placeholder='Почта'
                disabled
                rules={{ required: true, ...VALIDATION.emailFull }}
              />
            ),
          },
        ]
      : []),
    ...(vkId
      ? [
          {
            name: AuthSettingName.VK_ID,
            title: "VK ID",
            subtitle: "Вход через VK ID — данные подтягиваются автоматически",
            Icon: VkLogoIcon,
            disabled: true,
            fields: (
              <TextFieldControl
                name={`${fieldsNamePrefix}vkId`}
                label='VK ID'
                placeholder='VK ID'
                disabled
                rules={{ required: true, ...VALIDATION.inn }}
              />
            ),
          },
        ]
      : []),
  ];

  if (!authSettingsOptions.length) return null;

  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <FieldLabel label='Способы входа' />
        {isCreate && !viewMode && (
          <Card variant='dense'>
            <Typography>
              Для регистрации кандидата необходим <b>хотя бы один</b> способ входа. Подключите один или несколько.
            </Typography>
          </Card>
        )}
      </Stack>
      {authSettingsOptions.map((option) => (
        <SettingCard
          key={option.name}
          isEditing={isEditing}
          setting={option}
        />
      ))}
    </Stack>
  );
};

export default AuthSettings;
